import { useState, useMemo, useCallback } from 'react'
import type { JSX } from 'react'

import {
  Button,
  ExternalLinkIcon,
  Flex,
  Popover,
  Text,
} from '@buffer-mono/popcorn'

import { useCommentData } from '../../context/CommentContext'
import { useDelayedHover } from '../../../hooks/ui/useDelayedHover'

type PrivateAccountPopoverProps = {
  children: React.ReactNode
}

type UsePrivateAccountPopoverReturn = {
  PrivateAccountPopover: React.ComponentType<PrivateAccountPopoverProps>
  HoverWrapper: React.ComponentType<{ children: React.ReactNode }>
}

/**
 * Internal popover content component
 */
const PrivateAccountPopoverContent = ({
  onOpenPost,
  disabled,
}: {
  onOpenPost: () => void
  disabled: boolean
}): JSX.Element => (
  <Flex direction="column" gap="sm">
    <Text>
      This person&apos;s <strong>LinkedIn privacy settings</strong> protect
      their information, so we&apos;re unable to display their name or profile
      picture.
    </Text>
    <Flex justify="end" fullWidth>
      <Button
        variant="secondary"
        size="medium"
        onClick={onOpenPost}
        disabled={disabled}
      >
        Go to Comment <ExternalLinkIcon size="small" />
      </Button>
    </Flex>
  </Flex>
)

/**
 * Internal popover component - defined outside the hook to maintain stable
 * component identity and prevent children from remounting on parent re-renders.
 */
const PrivateAccountPopoverInternal = ({
  children,
  isPopoverOpen,
  setIsPopoverOpen,
  handleMouseEnter,
  handleMouseLeave,
  handleFocus,
  handleBlur,
}: PrivateAccountPopoverProps & {
  isPopoverOpen: boolean
  setIsPopoverOpen: (open: boolean) => void
  handleMouseEnter: () => void
  handleMouseLeave: () => void
  handleFocus: () => void
  handleBlur: () => void
}): JSX.Element => {
  const comment = useCommentData()

  const handleOpenPost = (): void => {
    if (comment.publicUrl) {
      window.open(comment.publicUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Popover.Anchor>
        <button
          type="button"
          aria-label="View privacy information"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onFocus={handleFocus}
          onBlur={handleBlur}
          style={{
            appearance: 'none',
            background: 'transparent',
            border: 'none',
            color: 'inherit',
            font: 'inherit',
            textAlign: 'inherit',
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {children}
        </button>
      </Popover.Anchor>
      <Popover.Content
        side="top"
        align="start"
        size="medium"
        arrow
        sideOffset={8}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onBlur={handleBlur}
        style={{ zIndex: 1000 }}
        data-private-account-popover="true"
      >
        <PrivateAccountPopoverContent
          onOpenPost={handleOpenPost}
          disabled={!comment.publicUrl}
        />
      </Popover.Content>
    </Popover>
  )
}

export const usePrivateAccountPopover = (): UsePrivateAccountPopoverReturn => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { handleMouseEnter, handleMouseLeave } = useDelayedHover({
    onOpen: () => setIsPopoverOpen(true),
    onClose: () => setIsPopoverOpen(false),
    isOpen: isPopoverOpen,
    openDelayDuration: 300,
  })

  // Keyboard handlers - open immediately on focus for better accessibility
  const handleFocus = useCallback((): void => {
    setIsPopoverOpen(true)
  }, [])

  const handleBlur = useCallback((): void => {
    // Delay closing to allow focus to move to popover content
    setTimeout(() => {
      if (
        !document.activeElement?.closest(
          '[data-private-account-popover="true"]',
        )
      ) {
        setIsPopoverOpen(false)
      }
    }, 100)
  }, [])

  // Memoize the wrapper component to maintain stable identity across re-renders
  // This prevents children from remounting when parent components re-render
  const PrivateAccountPopover = useMemo(() => {
    const Wrapper = ({ children }: PrivateAccountPopoverProps): JSX.Element => (
      <PrivateAccountPopoverInternal
        isPopoverOpen={isPopoverOpen}
        setIsPopoverOpen={setIsPopoverOpen}
        handleMouseEnter={handleMouseEnter}
        handleMouseLeave={handleMouseLeave}
        handleFocus={handleFocus}
        handleBlur={handleBlur}
      >
        {children}
      </PrivateAccountPopoverInternal>
    )
    Wrapper.displayName = 'PrivateAccountPopover'
    return Wrapper
  }, [
    isPopoverOpen,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
  ])

  const HoverWrapper = useMemo(() => {
    const Wrapper = ({
      children,
    }: {
      children: React.ReactNode
    }): JSX.Element => (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleFocus}
        onBlur={handleBlur}
        style={{ display: 'contents' }}
      >
        {children}
      </div>
    )
    Wrapper.displayName = 'HoverWrapper'
    return Wrapper
  }, [handleMouseEnter, handleMouseLeave, handleFocus, handleBlur])

  return { PrivateAccountPopover, HoverWrapper }
}
